import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Pagination,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'
import { ReloadOutlined } from '@ant-design/icons'
import { AlarmRecord, useUIStore } from '@/store/useDeviceStore'
import { get, put } from '@/utils/request'

const { RangePicker } = DatePicker

interface AlarmStats {
  days: number
  total: number
  unhandled: number
  by_level: Record<string, number>
  by_device: Array<{
    dev_id: string
    count: number
  }>
}

interface DeviceOption {
  dev_id: string
  name?: string
}

function AlarmCenter() {
  const { addAlarms, markAlarmHandled } = useUIStore()
  const [stats, setStats] = useState<AlarmStats | null>(null)
  const [devices, setDevices] = useState<DeviceOption[]>([])
  const [alarms, setAlarms] = useState<AlarmRecord[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [statsLoading, setStatsLoading] = useState(false)
  const [handlingIds, setHandlingIds] = useState<number[]>([])
  const [batchHandling, setBatchHandling] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [devId, setDevId] = useState<string>()
  const [level, setLevel] = useState<'warning' | 'danger'>()
  const [handled, setHandled] = useState<'all' | 'true' | 'false'>('all')
  const [timeRange, setTimeRange] = useState<[Dayjs, Dayjs] | null>(null)

  const loadDevices = useCallback(async () => {
    try {
      const result = await get<{ code: number; data: DeviceOption[] }>(
        '/api/v1/devices/list',
        { showError: false }
      )
      if (result.code === 0) {
        setDevices(result.data || [])
      }
    } catch (error) {
      console.error('加载设备列表失败:', error)
    }
  }, [])

  const loadStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const data = await get<AlarmStats>('/api/v1/alarms/stats?days=7', {
        showError: false,
      })
      setStats(data)
    } catch (error) {
      console.error('加载报警统计失败:', error)
    } finally {
      setStatsLoading(false)
    }
  }, [])

  const loadAlarms = useCallback(async () => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    })

    if (devId) {
      params.set('dev_id', devId)
    }
    if (level) {
      params.set('level', level)
    }
    if (handled !== 'all') {
      params.set('handled', handled)
    }
    if (timeRange) {
      params.set('start_time', timeRange[0].toISOString())
      params.set('end_time', timeRange[1].toISOString())
    }

    setLoading(true)
    try {
      const data = await get<{ total: number; items: AlarmRecord[] }>(
        `/api/v1/alarms?${params.toString()}`,
        { showError: false }
      )
      setAlarms(data.items)
      setTotal(data.total)
      addAlarms(data.items)
    } catch (error) {
      console.error('加载报警历史失败:', error)
      message.error('加载报警历史失败')
    } finally {
      setLoading(false)
    }
  }, [addAlarms, devId, handled, level, page, pageSize, timeRange])

  useEffect(() => {
    loadDevices()
    loadStats()
  }, [loadDevices, loadStats])

  useEffect(() => {
    loadAlarms()
  }, [loadAlarms])

  const refreshAll = async () => {
    await Promise.all([loadStats(), loadAlarms()])
  }

  const handleSingleAlarm = async (alarmId: number) => {
    setHandlingIds((prev) => [...prev, alarmId])
    try {
      await put(`/api/v1/alarms/${alarmId}/handle`)
      markAlarmHandled(alarmId)
      setAlarms((prev) =>
        prev.map((alarm) =>
          alarm.id === alarmId ? { ...alarm, handled: true } : alarm
        )
      )
      message.success('报警已处理')
      await loadStats()
    } catch (error) {
      // request 已统一处理
    } finally {
      setHandlingIds((prev) => prev.filter((id) => id !== alarmId))
    }
  }

  const handleBatch = async () => {
    const ids = selectedRowKeys.map((key) => Number(key))
    if (ids.length === 0) {
      message.warning('请先选择报警记录')
      return
    }

    try {
      await put('/api/v1/alarms/batch-handle', ids)
      ids.forEach((id) => markAlarmHandled(id))
      setAlarms((prev) =>
        prev.map((alarm) =>
          ids.includes(alarm.id) ? { ...alarm, handled: true } : alarm
        )
      )
      setSelectedRowKeys([])
      message.success(`已处理 ${ids.length} 条报警`)
      await loadStats()
    } catch (error) {
      // request 已统一处理
    }
  }

  const handleMarkCurrentPageRead = async () => {
    const ids = alarms.filter((alarm) => !alarm.handled).map((alarm) => alarm.id)

    if (ids.length === 0) {
      message.info('当前列表没有未读报警')
      return
    }

    setBatchHandling(true)
    try {
      await put('/api/v1/alarms/batch-handle', ids)
      ids.forEach((id) => markAlarmHandled(id))
      setAlarms((prev) =>
        prev.map((alarm) =>
          ids.includes(alarm.id) ? { ...alarm, handled: true } : alarm
        )
      )
      setSelectedRowKeys((prev) => prev.filter((id) => !ids.includes(id)))
      message.success(`已将当前列表 ${ids.length} 条报警标记为已读`)
      await loadStats()
    } catch (error) {
      // request 已统一处理
    } finally {
      setBatchHandling(false)
    }
  }

  const deviceOptions = useMemo(
    () =>
      devices.map((device) => ({
        value: device.dev_id,
        label: device.name || device.dev_id,
      })),
    [devices]
  )

  const columns: ColumnsType<AlarmRecord> = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '设备',
      dataIndex: 'dev_id',
      key: 'dev_id',
      width: 140,
    },
    {
      title: '级别',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (value: AlarmRecord['level']) => (
        <Tag color={value === 'danger' ? 'red' : 'orange'}>
          {value === 'danger' ? '危险' : '警告'}
        </Tag>
      ),
    },
    {
      title: '报警码',
      dataIndex: 'code',
      key: 'code',
      width: 100,
    },
    {
      title: '消息',
      dataIndex: 'msg',
      key: 'msg',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'handled',
      key: 'handled',
      width: 100,
      render: (value: boolean) => (
        <Tag color={value ? 'default' : 'processing'}>
          {value ? '已处理' : '未处理'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Button
          type="link"
          disabled={record.handled}
          loading={handlingIds.includes(record.id)}
          onClick={() => handleSingleAlarm(record.id)}
        >
          处理
        </Button>
      ),
    },
  ]

  return (
    <div className="page-shell" style={{ gap: 12, overflow: 'hidden' }}>
      <Row gutter={[12, 12]} style={{ flex: '0 0 auto' }}>
        <Col span={6}>
          <Card loading={statsLoading}>
            <Statistic title="近 7 天报警总数" value={stats?.total ?? 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={statsLoading}>
            <Statistic title="未处理报警" value={stats?.unhandled ?? 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={statsLoading}>
            <Statistic title="危险级别" value={stats?.by_level?.danger ?? 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={statsLoading}>
            <Statistic title="警告级别" value={stats?.by_level?.warning ?? 0} />
          </Card>
        </Col>
      </Row>

      <Card
        title="报警筛选"
        style={{ flex: '0 0 auto' }}
        extra={
          <Button icon={<ReloadOutlined />} onClick={refreshAll}>
            刷新
          </Button>
        }
      >
        <Space wrap>
          <Select
            allowClear
            placeholder="全部设备"
            style={{ width: 180 }}
            options={deviceOptions}
            value={devId}
            onChange={(value) => {
              setPage(1)
              setDevId(value)
            }}
          />
          <Select
            allowClear
            placeholder="全部级别"
            style={{ width: 140 }}
            value={level}
            onChange={(value) => {
              setPage(1)
              setLevel(value)
            }}
            options={[
              { value: 'warning', label: '警告' },
              { value: 'danger', label: '危险' },
            ]}
          />
          <Select
            style={{ width: 140 }}
            value={handled}
            onChange={(value) => {
              setPage(1)
              setHandled(value)
            }}
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'false', label: '未处理' },
              { value: 'true', label: '已处理' },
            ]}
          />
          <RangePicker
            showTime
            value={timeRange}
            onChange={(value) => {
              setPage(1)
              setTimeRange(value as [Dayjs, Dayjs] | null)
            }}
          />
          <Button onClick={handleBatch} disabled={selectedRowKeys.length === 0}>
            批量处理
          </Button>
          <Button
            onClick={handleMarkCurrentPageRead}
            loading={batchHandling}
            disabled={!alarms.some((alarm) => !alarm.handled)}
          >
            一键已读
          </Button>
          <Button
            onClick={() => {
              setPage(1)
              setDevId(undefined)
              setLevel(undefined)
              setHandled('all')
              setTimeRange(null)
            }}
          >
            重置筛选
          </Button>
        </Space>
      </Card>

      <Card
        title="报警历史"
        style={{ flex: '1 1 auto', minHeight: 0 }}
        styles={{
          body: {
            height: 'calc(100% - 57px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={alarms}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys.map((key) => Number(key))),
              getCheckboxProps: (record) => ({
                disabled: record.handled,
              }),
            }}
            pagination={false}
          />
        </div>
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            justifyContent: 'flex-end',
            paddingTop: 12,
            borderTop: '1px solid #f0f0f0',
            background: '#fff',
          }}
        >
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            onChange={(nextPage, nextPageSize) => {
              setPage(nextPage)
              setPageSize(nextPageSize)
            }}
          />
        </div>
      </Card>
    </div>
  )
}

export default AlarmCenter
